import type React from 'react';

import { Button } from '@change/ds-core-ui';
import { ShareIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core-react/i18n';

import type { PetitionDetailsShareCtaCopyExperimentVariation } from '#src/app/pages/petitionDetails/pageContext';
import type { UseMobileNativeShareReturn } from '#src/app/pages/petitionDetails/shared/hooks';

type Variation = PetitionDetailsShareCtaCopyExperimentVariation;

type Props = {
	shareAction: UseMobileNativeShareReturn;
	variation: Variation;
};

// Translation keys must be string literals for `@change-corgi/no-invalid-translation-keys`, so each
// variation's label is mapped explicitly here rather than built from a dynamic key. `control` reuses
// the existing, already-localized `fe.components.petition_share.action` key; `variant_1`/`variant_2`
// share a single `action_variant` key, since I18nOverrides only ever has one variant's resolved
// dictionary in scope at a time (see provider.tsx) — that key isn't in config/i18n/**/*.yml, it's
// supplied at runtime via I18nOverrides sourced from the petition_details_share_cta_copy_labels FC
// (see docs/packages/experiments/i18n-copy.md).
const VARIATION_LABELS: Readonly<Record<Variation, React.ReactNode>> = {
	control: <Translate value="fe.components.petition_share.action" />,
	// fake_control is a real, tracked bucket (A/A) but must render byte-identical copy to control.
	fake_control: <Translate value="fe.components.petition_share.action" />,
	// eslint-disable-next-line @change-corgi/no-invalid-translation-keys -- key comes from the petition_details_share_cta_copy_labels FC's I18nOverrides
	variant_1: <Translate value="fe.components.petition_share.action_variant" />,
	// eslint-disable-next-line @change-corgi/no-invalid-translation-keys -- key comes from the petition_details_share_cta_copy_labels FC's I18nOverrides
	variant_2: <Translate value="fe.components.petition_share.action_variant" />,
};

/**
 * Fires the same OS native share sheet as the hero-photo `NativeShareMobile` FAB — no modal,
 * no `useMobileModalContext`, no `ShareEducationModalProvider`, since the native-share path needs
 * none of them. `shareAction` is resolved once by the caller and passed down here, rather than
 * re-derived via a second `useMobileNativeShare` call with the same tracking context.
 */
export const SignCtaShareAffordance: React.FC<Props> = ({ shareAction, variation }) => {
	// Render nothing when the resolved share action is unavailable — never a dead button.
	if (!shareAction) {
		return null;
	}

	const { ShareAction } = shareAction;

	return (
		<ShareAction>
			<Button
				leadingIcon={<ShareIcon />}
				variant="secondary"
				size="large"
				data-qa="sign-cta-share-affordance"
				data-testid="sign-cta-share-affordance"
				className="w-full max-w-none"
			>
				{VARIATION_LABELS[variation]}
			</Button>
		</ShareAction>
	);
};
